import firebase from 'firebase/app'
import 'firebase/database'

// Culture Index rules from the Happily-Company Insights Report Card (the
// product source of truth), so Monthly/Quarterly Insights show the same Culture
// score as the Report Card, happily-api and happily-mcp.

export const DEFAULT_CULTURE_QUESTION_IDS = [
    '-MjPYFeALtFMAJkTEiFn',
    '-MjPZ15ImFhkjDiMI3gH',
    '-MjPZVq3sRBZJwtr7_Tx',
    '-MjP_2__thuvnVc_RY0k',
    '-MjP_R_bWBdQvFhf5BTY'
]
const RESERVED_SET_QUESTION_IDS = [
    // WHO-5
    '-MdMv_XgThL0o2iunlhE', '-MdMw8kfpZtxnppGUA1f', '-MdMyavdF-YvuyzW6cA5', '-MdMz-TodEXbj3AC68oP', '-MdMzwfySVTjhOAGoxTP',
    // eRes
    '-MgaSDDhL-3mt78QHuYL', '-MgaSw_L3eW0WyF4ZplB', '-MgaTQObIM08enQK5p3F', '-MgaU6mpwzOgerV7w-IW', '-MgaUfGLnzhU0BcUv5Db',
    ...DEFAULT_CULTURE_QUESTION_IDS
]

// Default Culture questions weight answer index 0..5 as 1/.5/.25/0/0/0.
const DEFAULT_CULTURE_WEIGHTS = [1, 0.5, 0.25, 0, 0, 0]

/** Custom Culture answer weight (Happily-Company globalfunc.getCultureOptionScore). */
export function cultureOptionWeight(question: any, answerIndex: number): number {
    if (question && question.type === 'Scale') {
        if (answerIndex >= 9) return 1
        if (answerIndex >= 7) return 0.5
        if (answerIndex >= 4) return 0.25
        return 0
    }
    // A 6-point scale has no neutral midpoint, so its positives grade 100/75/50.
    const choice = (question && question.m_choice) || {}
    const feelings: Array<any> = Array.isArray(choice.feeling) ? choice.feeling : []
    const optionCount = Math.max(Array.isArray(choice.en) ? choice.en.length : 0, feelings.length, answerIndex + 1)
    const strengthWeights = optionCount >= 6 ? [1, 0.75, 0.5, 0, 0, 0] : [1, 0.5, 0.25, 0, 0]
    const feeling = feelings[answerIndex]
    if (optionCount <= 2 && feeling) {
        if (feeling === 'Positive') return 1
        if (feeling === 'Neutral') return 0.5
        return 0
    }
    if (feeling === 'Positive') {
        if (optionCount >= 6) {
            const positional = [1, 0.75, 0.5][answerIndex]
            return positional === undefined ? 0.5 : positional
        }
        const positiveIndexes = feelings.map((value, index) => (value === 'Positive' ? index : -1)).filter(index => index >= 0)
        const position = positiveIndexes.indexOf(answerIndex)
        return positiveIndexes.length <= 1 || position <= 0 ? 1 : 0.5
    }
    if (feeling === 'Neutral') return 0.25
    if (feeling === 'Negative') return 0
    if (choice.weight && choice.weight[answerIndex] !== undefined) {
        const weight = Number(choice.weight[answerIndex])
        if (!isNaN(weight)) return weight > 1 ? weight / 100 : weight
    }
    if (optionCount >= 5 && strengthWeights[answerIndex] !== undefined) return strengthWeights[answerIndex]
    return optionCount > 1 ? (optionCount - 1 - answerIndex) / (optionCount - 1) : 0
}

/**
 * Custom Culture questions for a company: questions from `main` or the company
 * that carry culture_index. A culture-only question (not a Report Card row)
 * always counts; a Report Card row counts unless it is an NPS question or part
 * of the WHO-5 / eRes / default Culture sets.
 */
export function selectCustomCultureQuestionIds(questions: Record<string, any>, company: string): string[] {
    return Object.keys(questions || {}).filter(questionId => {
        const data = questions[questionId]
        if (!data || data.culture_index !== true) return false
        if (data.company !== 'main' && data.company !== company) return false
        if (data.report_card !== 'Y') return true
        return data.nps === undefined && RESERVED_SET_QUESTION_IDS.indexOf(questionId) < 0
    })
}

/** Report Card question catalog: report_card = 'Y' plus every culture_index question. */
export async function fetchCultureQuestionCatalog(company: string): Promise<{ customIds: string[]; questions: Record<string, any> }> {
    const questionsRef = firebase.database().ref('questions')
    const [reportCardSnapshot, cultureIndexSnapshot] = await Promise.all([
        questionsRef.orderByChild('report_card').equalTo('Y').once('value'),
        questionsRef.orderByChild('culture_index').equalTo(true).once('value')
    ])
    const questions: Record<string, any> = { ...(cultureIndexSnapshot.val() || {}), ...(reportCardSnapshot.val() || {}) }
    return { customIds: selectCustomCultureQuestionIds(questions, company), questions }
}

/** The questions a Culture score uses: the custom set when there is one, else the 5 defaults. */
export function cultureQuestionSet(customIds: string[]): string[] {
    return customIds && customIds.length > 0 ? customIds : [...DEFAULT_CULTURE_QUESTION_IDS]
}

/**
 * Report Card Culture score (0-100, 1 decimal) pooled over the records of one
 * scope; NaN when unavailable. The default set needs all 5 questions; a custom
 * set may be partial.
 */
export function calculateReportCardCulture(records: Array<any>, questions: Record<string, any>, customIds: string[]): number {
    const useCustom = !!(customIds && customIds.length > 0)
    const ids = cultureQuestionSet(customIds)
    const scoped = (records || []).filter(record => record && record.answer && Object.keys(record.answer).length > 0 && ids.indexOf(record.quest_id) >= 0)
    if (!useCustom && !DEFAULT_CULTURE_QUESTION_IDS.every(id => scoped.some(record => record.quest_id === id))) return NaN
    let dataSum = 0
    let total = 0
    scoped.forEach(record => {
        Object.keys(record.answer).forEach(answerKey => {
            const count = Number(record.answer[answerKey]) || 0
            if (count <= 0) return
            const answerIndex = Number(answerKey)
            const weight = useCustom
                ? cultureOptionWeight(questions[record.quest_id] || {}, answerIndex)
                : DEFAULT_CULTURE_WEIGHTS[answerIndex] || 0
            dataSum += count * weight
            total += count
        })
    })
    return total > 0 ? Math.round(((dataSum * 100) / total) * 10) / 10 : NaN
}

/** Culture score for one team in a quarter map keyed `${team}_${questionId}`; NaN when unavailable. */
export function cultureScoreForTeam(quarterMap: any, team: string, questions: Record<string, any>, customIds: string[]): number {
    if (!quarterMap || !team) return NaN
    const records = cultureQuestionSet(customIds).map(id => quarterMap[team + '_' + id]).filter(Boolean)
    return calculateReportCardCulture(records, questions, customIds)
}
